import axios from 'axios';
import { WrittenContribution, Comment } from '../hooks/useWrittenContributions';
import { API_ROOT } from '.';

// contributions endpoints mounted under /api/contributions
const CONTRIBUTIONS_API_URL = `${API_ROOT}/contributions`;

export async function callFetchContributions(): Promise<WrittenContribution[]> {
  const res = await axios.get<WrittenContribution[]>(CONTRIBUTIONS_API_URL);
  return res.data;
}

export async function callCreateContribution(
  data: {
    title: string;
    fileUrl: string;
    publicId: string;
    mimeType: string;
    fileSizeBytes: number;
    summary?: string;
    isDownloadable?: boolean;
    publicationCover: string;
    publicationCoverPublicId?: string;
    publicationCoverMimeType?: string;
    publicationCoverSizeBytes?: number;
  },
  token: string
): Promise<WrittenContribution> {
  const res = await axios.post<WrittenContribution>(CONTRIBUTIONS_API_URL, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function callUploadPdfFile(
  file: File,
  token: string,
  extraFields?: Record<string, string>
): Promise<WrittenContribution> {
  const form = new FormData();
  form.append('file', file);
  if (extraFields) {
    for (const key of Object.keys(extraFields)) {
      form.append(key, extraFields[key]);
    }
  }
  const res = await axios.post<WrittenContribution>(`${CONTRIBUTIONS_API_URL}/upload`, form, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
}

export async function callUploadCover(file: File, token: string) {
  const form = new FormData();
  form.append('cover', file);
  const res = await axios.post(`${CONTRIBUTIONS_API_URL}/upload-cover`, form, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
}

export async function callEditContributionTitle(
  id: string,
  title: string,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.put<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/title`,
    { title },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callEditContributionSummary(
  id: string,
  summary: string,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.put<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/summary`,
    { summary },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callEditContributionDownloadable(
  id: string,
  isDownloadable: boolean,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.put<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/downloadable`,
    { isDownloadable },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callDeleteContribution(
  id: string,
  token: string
): Promise<{ message: string }> {
  const res = await axios.delete<{ message: string }>(`${CONTRIBUTIONS_API_URL}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function callFetchComments(
  id: string,
  token: string
): Promise<Comment[]> {
  const res = await axios.get<Comment[]>(`${CONTRIBUTIONS_API_URL}/${id}/comments`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function callAddComment(
  id: string,
  text: string,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.post<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/comments`,
    { text },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callEditComment(
  id: string,
  commentId: string,
  text: string,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.put<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/comments/${commentId}`,
    { text },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callDeleteComment(
  id: string,
  commentId: string,
  token: string
): Promise<WrittenContribution> {
  const res = await axios.delete<WrittenContribution>(
    `${CONTRIBUTIONS_API_URL}/${id}/comments/${commentId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
}

export async function callFetchContributionBySlug(slug: string): Promise<WrittenContribution> {
  const res = await axios.get<WrittenContribution>(`${CONTRIBUTIONS_API_URL}/slug/${slug}`);
  return res.data;
}
